import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import ms from 'ms';

import { ENDPOINT } from '~/constants/endpoints';

import { type Address } from '~/types/address';
import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysSite } from './queryKeys';

export type SiteIdentifierProposal = {
  address: Address;
  identifier: string;
  proposalUuid: UUID;
  supplierId: UUID;
  supplierName: string;
};

export type SiteIdentifierProposalsResponse = SiteIdentifierProposal[];

/**
 * Fetches site identifier proposals from the API.
 * @returns {Promise<SiteIdentifierProposalsResponse>} Array of site identifier proposals.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Site/operation/get_identifier_proposals_site_identifier_proposals_get
 */
export const fetchSiteIdentifierProposals = async () => {
  const response = await vestigasApi
    .get(ENDPOINT.SITE.GET_IDENTIFIER_PROPOSALS())
    .json<SiteIdentifierProposalsResponse>();

  return response ?? [];
};

/**
 * React Query based custom hook for getting site identifier proposals.
 * @param {Object} options - Additional options for the useQuery hook.
 * @returns {UseQueryResult<SiteIdentifierProposalsResponse>} The result of the useQuery hook.
 */
export const useQuerySiteIdentifierProposals = <
  TSelected = SiteIdentifierProposalsResponse,
>(
  options?: Omit<
    UseQueryOptions<SiteIdentifierProposalsResponse, unknown, TSelected>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery<SiteIdentifierProposalsResponse, unknown, TSelected>({
    queryFn: fetchSiteIdentifierProposals,
    queryKey: queryKeysSite.getSiteIdentifierProposals(),
    staleTime: ms('60s'), // prevent hitting the rate limit of 30 requests/s
    ...options,
  });
